<template>
  <v-app-bar
    id="app-bar"
    app
    flat
    height="64"
    :color="$vuetify.theme.dark ? '#0D0D0D' : '#FFFFFF'"
    class="elevment-app-bar"
  >
    <v-btn icon :color="$vuetify.theme.dark ? 'white' : 'grey darken-3'" class="mr-3" @click="setDrawer(!drawer)">
      <v-icon>{{ value ? 'mdi-view-quilt' : 'mdi-dots-vertical' }}</v-icon>
    </v-btn>

    <div class="d-flex align-center ml-2">
      <span class="text-body-2 text--secondary font-weight-medium">{{ departmentName }}</span>
      <v-icon small color="grey" class="mx-1">mdi-chevron-right</v-icon>
      <span class="text-body-2 text--primary font-weight-bold">{{ currentRouteName }}</span>
    </div>

    <v-spacer />

    <v-text-field
      dense
      flat
      hide-details
      solo
      :background-color="$vuetify.theme.dark ? '#1A1A1A' : '#F5F5F7'"
      placeholder="Buscar..."
      prepend-inner-icon="mdi-magnify"
      class="search-bar mr-4"
      color="primary"
    >
      <template v-slot:append>
        <div class="shortcut-box">⌘K</div>
      </template>
    </v-text-field>

    <v-menu
      bottom left offset-y origin="top right" transition="scale-transition"
      max-width="380" min-width="360"
      content-class="elevation-10 rounded-lg notif-menu"
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ attrs, on }">
        <v-btn icon v-bind="attrs" v-on="on" :color="$vuetify.theme.dark ? 'white' : 'grey darken-3'" class="mr-2">
          <v-badge color="#22d3ee" overlap bordered :content="unreadCount" :value="unreadCount > 0" class="notif-badge">
            <v-icon>mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>
      </template>

      <div class="notif-panel">
        <!-- Header -->
        <div class="notif-header d-flex align-center px-4 py-3">
          <span class="notif-title">{{ $t('appbar.notifications') }}</span>
          <span v-if="unreadCount > 0" class="notif-count ml-2">{{ unreadCount }}</span>
          <v-spacer />
          <v-btn text small class="notif-markall" :disabled="unreadCount === 0" @click="markAllRead">
              <v-icon left small>mdi-check-all</v-icon>
              {{ $t('appbar.markAll') }}
            </v-btn>
        </div>
        <v-divider class="notif-divider" />

        <!-- Lista -->
        <div class="notif-list">
          <template v-if="recentNotifications.length">
            <div
              v-for="n in recentNotifications"
              :key="n.id"
              class="notif-item d-flex align-start px-4 py-3"
              @click="handleNotificationClick(n)"
            >
              <div class="notif-icon-box mr-3" :style="{ backgroundColor: notifColor(n) + '22' }">
                <v-icon size="18" :color="notifColor(n)">{{ notifIcon(n) }}</v-icon>
              </div>
              <div class="notif-body">
                <div class="d-flex align-center">
                  <span class="notif-item-title">{{ n.title }}</span>
                  <span v-if="!n.read" class="notif-dot ml-2"></span>
                </div>
                <div class="notif-item-msg">{{ n.message || n.title }}</div>
                <div class="notif-item-time">{{ timeAgo(n.createdAt) }}</div>
              </div>
            </div>
          </template>
          <div v-else class="notif-empty text-center">
            <v-icon size="40" color="grey">mdi-bell-off-outline</v-icon>
            <div class="notif-empty-text mt-2">{{ $t('appbar.noNotifications') }}</div>
          </div>
        </div>

        <v-divider class="notif-divider" />
        <!-- Footer -->
        <div class="notif-footer" @click="goToNotifications">
          {{ $t('appbar.viewAll') }}
        </div>
      </div>
    </v-menu>


  </v-app-bar>
</template>

<script>
  import { NotificationsService } from '@/services'
  import { mapState, mapMutations } from 'vuex'

  export default {
    name: 'DashboardCoreAppBar',

    props: {
      value: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      notifications: [],
      pollInterval: null,
    }),

    computed: {
      ...mapState(['drawer']),
      
      departmentName() {
        try {
          const userStr = localStorage.getItem('user')
          if (userStr) {
            const user = JSON.parse(userStr)
            return user.departmentName || user.department?.name || 'ElevMent'
          }
        } catch (error) {
          console.error('Erro ao ler departamento do usuário:', error)
        }
        return 'ElevMent'
      },

      currentRouteName() {
        return this.$route.name || 'Dashboard'
      },
      
      unreadCount() {
        if (!this.notifications || !Array.isArray(this.notifications)) return 0
        return this.notifications.filter(n => !n.read).length
      },

      // Mostra apenas as mais recentes no dropdown (a lista completa fica em /notifications)
      recentNotifications() {
        if (!this.notifications || !Array.isArray(this.notifications)) return []
        return this.notifications.slice(0, 6)
      },

    },

    mounted() {
      this.$nextTick(() => { this.loadNotifications() })
      this.pollInterval = setInterval(() => { this.loadNotifications() }, 60000)
    },

    beforeDestroy() {
      if (this.pollInterval) clearInterval(this.pollInterval)
    },

    methods: {
      ...mapMutations({ setDrawer: 'SET_DRAWER' }),

      async loadNotifications() {
        try {
          const response = await NotificationsService.list()
          this.notifications = (response.data || []).sort((a, b) => {
            return new Date(b.createdAt) - new Date(a.createdAt)
          })
        } catch (error) {
          if (!this.notifications) this.notifications = []
        }
      },

      goToNotifications() {
        if (this.$route.path !== '/notifications') {
          this.$router.push('/notifications')
        }
      },

      // --- Helpers visuais (espelham a tela de notificações) ---
      displayTypeOf(notification) {
        const typeMap = {
          ONE_ON_ONE: 'meeting',
          PDI: 'pdi',
          FEEDBACK: 'feedback',
          ACHIEVEMENT: 'achievement',
          CAMPAIGN: 'general',
          APPROVAL: 'approval',
          APPROVAL_NEW: 'approval',
          APPROVAL_COMPLETION: 'approval',
        }
        if (notification.type && typeMap[notification.type]) return typeMap[notification.type]

        const title = (notification.title || '').toLowerCase()
        if (title.includes('feedback')) return 'feedback'
        if (title.includes('pdi') || title.includes('certificação')) return 'pdi'
        if (title.includes('reunião') || title.includes('1:1')) return 'meeting'
        if (title.includes('conquista') || title.includes('badge')) return 'achievement'
        if (title.includes('aprovação') || title.includes('aprovar') || title.includes('pendente')) return 'approval'
        return 'general'
      },

      notifColor(notification) {
        const colors = {
          meeting: '#22d3ee',
          feedback: '#38bdf8',
          pdi: '#a855f7',
          achievement: '#f59e0b',
          approval: '#fb923c',
          general: '#fb923c',
        }
        return colors[this.displayTypeOf(notification)] || '#22d3ee'
      },

      notifIcon(notification) {
        const icons = {
          meeting: 'mdi-calendar-outline',
          feedback: 'mdi-message-text-outline',
          pdi: 'mdi-bullseye-arrow',
          achievement: 'mdi-trophy-outline',
          approval: 'mdi-clipboard-check-outline',
          general: 'mdi-bullhorn-outline',
        }
        return icons[this.displayTypeOf(notification)] || 'mdi-bell-outline'
      },

      timeAgo(createdAt) {
        if (!createdAt) return 'Agora'
        const now = new Date()
        const date = new Date(createdAt)
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)
        if (diffMins < 1) return 'Agora'
        if (diffMins < 60) return `há ${diffMins}min`
        if (diffHours < 24) return `há ${diffHours} horas`
        if (diffDays === 1) return 'ontem'
        if (diffDays < 7) return `há ${diffDays} dias`
        return date.toLocaleDateString('pt-BR')
      },

      async markAllRead() {
        try {
          const unreadIds = this.notifications.filter(n => !n.read).map(n => n.id)
          if (unreadIds.length === 0) return
          await NotificationsService.markAllAsRead(unreadIds)
          this.notifications.forEach(n => { n.read = true })
        } catch (error) {
          console.error('Erro ao marcar todas as notificações:', error)
        }
      },

      handleNotificationClick(notification) {
        const typeRouteMap = {
          FEEDBACK: '/feedbacks',
          PDI: '/pdis',
          ONE_ON_ONE: '/one-on-ones',
          MEETING: '/one-on-ones',
          APPROVAL: '/approval',
          APPROVAL_NEW: '/approval?tab=0&status=PENDING_APPROVAL',
          APPROVAL_COMPLETION: '/approval?tab=0&status=PENDING_COMPLETION',
          CAMPAIGN: '/campaigns',
        }

        // Marca como lida (não bloqueante) preservando a integração com o back
        if (!notification.read) {
          NotificationsService.markAsRead(notification.id)
            .then(() => { notification.read = true })
            .catch(error => console.error('Erro ao marcar notificação:', error))
        }

        const route = typeRouteMap[(notification.type || '').toUpperCase()] || '/notifications'
        if (this.$route.fullPath !== route) {
          this.$router.push(route).catch(() => {})
        }
      },

    },
  }
</script>

<style scoped>
.theme--dark .elevment-app-bar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
  background-color: #0D0D0D !important;
}
.theme--light .elevment-app-bar { border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important; }

.search-bar { max-width: 240px; border-radius: 6px; }
.search-bar ::v-deep .v-input__control { min-height: 36px !important; }

.theme--dark .search-bar { border: 1px solid rgba(255, 255, 255, 0.08); }
.theme--dark .search-bar ::v-deep input { font-size: 0.85rem !important; color: white !important; }
.theme--light .search-bar { border: 1px solid rgba(0, 0, 0, 0.08); }
.theme--light .search-bar ::v-deep input { font-size: 0.85rem !important; color: #1D1D1F !important; }

.shortcut-box { border-radius: 4px; padding: 2px 6px; font-size: 0.7rem; font-weight: 500; }
.theme--dark .shortcut-box { background-color: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1); color: #9E9E9E; }
.theme--light .shortcut-box { background-color: rgba(0, 0, 0, 0.04); border: 1px solid rgba(0, 0, 0, 0.1); color: #86868B; }


/* ════════════════════════════════════════════════════════
   PAINEL DE NOTIFICAÇÕES
   ════════════════════════════════════════════════════════ */
.notif-badge ::v-deep .v-badge__badge {
  color: #06222b !important;
  font-weight: 700;
  font-size: 0.65rem;
}

.notif-panel { overflow: hidden; }

.theme--dark .notif-menu { background-color: #161616 !important; border: 1px solid rgba(255, 255, 255, 0.06); }
.theme--light .notif-menu { background-color: #FFFFFF !important; border: 1px solid rgba(0, 0, 0, 0.06); }

/* Header */
.notif-title { font-size: 0.95rem; font-weight: 700; }
.theme--dark .notif-title { color: #f5f5f5; }
.theme--light .notif-title { color: #1D1D1F; }

.notif-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background-color: #22d3ee;
  color: #06222b;
  font-size: 0.72rem;
  font-weight: 700;
}

.notif-markall {
  letter-spacing: 0 !important;
  text-transform: none !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  color: #22d3ee !important;
  padding: 0 6px !important;
  min-width: auto !important;
}
.notif-markall.v-btn--disabled { color: rgba(128, 128, 128, 0.5) !important; }

.notif-divider { border-color: rgba(128, 128, 128, 0.16) !important; }

/* Lista */
.notif-list { max-height: 360px; overflow-y: auto; }

.notif-item { cursor: pointer; transition: background-color 0.15s ease; }
.theme--dark .notif-item:hover { background-color: rgba(255, 255, 255, 0.04); }
.theme--light .notif-item:hover { background-color: rgba(0, 0, 0, 0.03); }
.notif-item + .notif-item { border-top: 1px solid rgba(128, 128, 128, 0.1); }

.notif-icon-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.notif-body { min-width: 0; flex: 1; }

.notif-item-title {
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.25;
}
.theme--dark .notif-item-title { color: #f5f5f5; }
.theme--light .notif-item-title { color: #1D1D1F; }

.notif-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #22d3ee;
  flex-shrink: 0;
}

.notif-item-msg {
  font-size: 0.8rem;
  line-height: 1.35;
  margin-top: 2px;
  color: #888888;
}

.notif-item-time {
  font-size: 0.72rem;
  margin-top: 4px;
  color: #6b6b6b;
}

/* Estado vazio */
.notif-empty { padding: 32px 16px; }
.notif-empty-text { font-size: 0.85rem; color: #888888; }

/* Footer */
.notif-footer {
  text-align: center;
  padding: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #22d3ee;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.theme--dark .notif-footer:hover { background-color: rgba(34, 211, 238, 0.08); }
.theme--light .notif-footer:hover { background-color: rgba(34, 211, 238, 0.08); }

/* Scrollbar fino */
.notif-list::-webkit-scrollbar { width: 6px; }
.notif-list::-webkit-scrollbar-track { background: transparent; }
.theme--dark .notif-list::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.14); border-radius: 3px; }
.theme--light .notif-list::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.18); border-radius: 3px; }
</style>